import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, Zap, PackageOpen, CheckCircle, AlertCircle, Package, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import { inventarioService } from '@/services/inventario.service';
import { sucursalesService } from '@/services/sucursales.service';
import { productosService } from '@/services/productos.service';
import { useAuthStore } from '@/stores/auth-store';
import { useToastStore } from '@/stores/toast-store';

export function QuickEntryPage() {
  const { user } = useAuthStore();
  const { addToast } = useToastStore();
  
  // Modos de operación
  const [operationMode, setOperationMode] = useState<'in' | 'out'>('in');
  const [branches, setBranches] = useState<any[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  
  const [barcode, setBarcode] = useState('');
  const [product, setProduct] = useState<any>(null);
  const [quantity, setQuantity] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Autocomplete state
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const qtyRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user?.company?.id) {
      sucursalesService.findByCompany(user.company.id).then(b => {
        setBranches(b);
        if (b.length > 0) setSelectedBranchId(b[0].id);
      }).catch(() => {});
    }
  }, [user?.company?.id]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [operationMode]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const performSearch = async (code: string) => {
    if (!code.trim() || !user?.company?.id) return;
    
    setIsLoading(true);
    setError('');
    try {
      const data = await productosService.findByBarcode(code, user.company.id);
      if (data) {
        selectProduct(data);
      } else {
        setProduct(null);
        setError('Producto no encontrado');
        inputRef.current?.focus();
      }
    } catch (err: any) {
      setProduct(null);
      setError(err.response?.data?.message || 'Error al buscar producto');
      inputRef.current?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const autocompleteSearch = useCallback(async (query: string) => {
    if (!query.trim() || !user?.company?.id) {
      setSearchResults([]);
      setSelectedIndex(-1);
      return;
    }
    setSearchLoading(true);
    try {
      const result = await productosService.findAll({ 
        companyId: user.company.id, 
        search: query,
        limit: 10
      });
      const items = result.data || result || [];
      setSearchResults(items);
      setSelectedIndex(items.length > 0 ? 0 : -1);
    } catch {
      setSearchResults([]);
      setSelectedIndex(-1);
    } finally {
      setSearchLoading(false);
    }
  }, [user?.company?.id]);

  const handleBarcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBarcode(value);
    setShowDropdown(true);
    setSelectedIndex(-1);
    
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => autocompleteSearch(value), 300);
  };

  const selectProduct = (selectedProduct: any) => {
    setProduct(selectedProduct);
    setBarcode(selectedProduct.barcode || selectedProduct.sku || selectedProduct.name);
    setShowDropdown(false);
    setSelectedIndex(-1);
    setQuantity('1'); // Default quantity
    setTimeout(() => qtyRef.current?.focus(), 100);
  };

  const handleBarcodeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!showDropdown) setShowDropdown(true);
      if (searchResults.length > 0) {
        setSelectedIndex(prev => (prev < searchResults.length - 1 ? prev + 1 : 0));
      }
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!showDropdown) setShowDropdown(true);
      if (searchResults.length > 0) {
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : searchResults.length - 1));
      }
      return;
    }

    if (e.key === 'Escape') {
      setShowDropdown(false);
      setSelectedIndex(-1);
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      clearTimeout(debounceRef.current);
      if (showDropdown && selectedIndex >= 0 && searchResults[selectedIndex]) {
        selectProduct(searchResults[selectedIndex]);
      } else if (showDropdown && searchResults.length > 0) {
        selectProduct(searchResults[0]);
      } else {
        setShowDropdown(false);
        performSearch(barcode);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!product || !quantity || isNaN(Number(quantity))) return;

    const qty = parseInt(quantity, 10);
    if (qty <= 0) {
      setError('La cantidad debe ser mayor a cero');
      return;
    }

    const currentStock = product.stock || 0;
    
    if (operationMode === 'out' && qty > currentStock) {
      setError(`Stock insuficiente. Solo hay ${currentStock} piezas disponibles.`);
      return;
    }

    setIsLoading(true);
    try {
      const qtyToSend = operationMode === 'in' ? qty : -qty;

      const result = await inventarioService.adjust({
        companyId: user?.company?.id || '',
        branchId: selectedBranchId,
        productId: product.id,
        quantity: qtyToSend,
        type: operationMode === 'in' ? 'IN' : 'OUT',
        reason: 'Movimiento Rápido'
      });
      
      const newStock = result.newStock !== undefined ? result.newStock : (operationMode === 'in' ? currentStock + qty : currentStock - qty);
      
      const successMessage = operationMode === 'in'
        ? `Se agregaron ${qty} piezas a "${product.name}". Nuevo stock: ${newStock}`
        : `Se restaron ${qty} piezas a "${product.name}". Nuevo stock: ${newStock}`;

      addToast(successMessage, 'success');

      // Reset form for next scan
      setProduct(null);
      setBarcode('');
      setQuantity('');
      setError('');
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al actualizar el stock');
    } finally {
      setIsLoading(false);
    }
  };

  const isInputMode = operationMode === 'in';

  return (
    <div className="w-full space-y-6 p-4 md:p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Zap className={`h-6 w-6 ${isInputMode ? 'text-green-500' : 'text-orange-500'}`} />
            Movimientos Rápidos
          </h1>
          <p className="text-muted-foreground mt-1">
            Escanea o busca productos para ajustar inventario al vuelo
          </p>
        </div>
        
        {/* Toggle Mode */}
        <div className="flex bg-muted p-1 rounded-lg self-stretch md:self-auto">
          <button
            onClick={() => setOperationMode('in')}
            className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${
              isInputMode ? 'bg-background shadow-sm text-green-600' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <ArrowUpCircle size={18} />
            Entrada
          </button>
          <button
            onClick={() => setOperationMode('out')}
            className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${
              !isInputMode ? 'bg-background shadow-sm text-orange-600' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <ArrowDownCircle size={18} />
            Salida
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Scanner Panel */}
        <div className={`bg-card text-card-foreground rounded-xl border shadow-sm p-6 space-y-6 border-t-4 ${isInputMode ? 'border-t-green-500' : 'border-t-orange-500'}`}>
          <div className="space-y-4">
            <label className="text-sm font-medium">Buscar por Nombre, Código o SKU</label>
            <div className="relative" ref={containerRef}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                <Search className="h-5 w-5 text-muted-foreground" />
              </div>
              <input
                ref={inputRef}
                type="text"
                className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-lg pl-10 ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Escanea o escribe aquí..."
                value={barcode}
                onChange={handleBarcodeChange}
                onKeyDown={handleBarcodeKeyDown}
                onFocus={() => {
                  if (barcode.trim().length > 0) setShowDropdown(true);
                }}
                disabled={isLoading}
                autoComplete="off"
              />
              
              {/* Autocomplete Dropdown */}
              {showDropdown && barcode.trim().length > 0 && (
                <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-zinc-950 border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  {searchLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                    </div>
                  ) : searchResults.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground text-sm">
                      No se encontraron productos
                    </div>
                  ) : (
                    searchResults.map((item: any, index: number) => {
                      const isSelected = index === selectedIndex;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onMouseEnter={() => setSelectedIndex(index)}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            selectProduct(item);
                          }}
                          className={`w-full text-left px-4 py-3 transition-colors border-b last:border-b-0 flex items-center justify-between group ${
                            isSelected
                              ? 'bg-blue-50 border-l-4 border-l-blue-600 dark:bg-blue-950/40'
                              : 'hover:bg-muted'
                          }`}
                        >
                          <div className="flex flex-col">
                            <span className={`font-medium transition-colors ${isSelected ? 'text-blue-700 dark:text-blue-400 font-bold' : 'group-hover:text-primary'}`}>
                              {item.name}
                            </span>
                            <span className="text-xs text-muted-foreground mt-0.5">
                              SKU: {item.sku} {item.barcode ? `| Cód: ${item.barcode}` : ''}
                            </span>
                          </div>
                          <div className="text-right">
                            <span className="block font-bold text-primary">${item.pricePublic}</span>
                            <span className="text-xs text-muted-foreground block mt-0.5">Stock: {item.stock || 0}</span>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
            {error && (
              <div className="flex items-center gap-2 text-destructive text-sm font-medium mt-2">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Puedes escanear con tu pistola (presionará Enter por ti) o teclear el nombre para autocompletar.
            </p>
          </div>
        </div>

        {/* Action Panel */}
        <div className="bg-card text-card-foreground rounded-xl border shadow-sm p-6">
          {product ? (
            <form onSubmit={handleSubmit} className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
              <div className="p-4 bg-muted/50 rounded-lg border">
                <h3 className="font-bold text-xl mb-1">{product.name}</h3>
                <div className="flex gap-4 text-sm text-muted-foreground mt-2">
                  <span>SKU: {product.sku}</span>
                  <span>Stock Actual: <strong className="text-foreground">{product.stock || 0}</strong></span>
                </div>
              </div>

              <div className="space-y-4">
                <label className="text-sm font-medium">
                  {isInputMode ? 'Cantidad a Agregar (+)' : 'Cantidad a Restar (-)'}
                </label>
                <input
                  ref={qtyRef}
                  type="number"
                  min="1"
                  className={`flex h-16 w-full rounded-md border bg-background px-4 py-2 text-3xl font-bold text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                    isInputMode ? 'border-green-200 focus-visible:ring-green-500' : 'border-orange-200 focus-visible:ring-orange-500'
                  }`}
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || !quantity}
                className={`w-full h-14 text-white inline-flex items-center justify-center rounded-md text-lg font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${
                  isInputMode 
                    ? 'bg-green-600 hover:bg-green-700 focus-visible:ring-green-600' 
                    : 'bg-orange-600 hover:bg-orange-700 focus-visible:ring-orange-600'
                }`}
              >
                {isLoading ? 'Guardando...' : (
                  <>
                    <CheckCircle className="mr-2 h-6 w-6" />
                    {isInputMode ? 'Confirmar Entrada' : 'Confirmar Salida'}
                  </>
                )}
              </button>
            </form>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-3 text-muted-foreground min-h-[250px]">
              <PackageOpen className="h-16 w-16 opacity-20" />
              <p>Esperando producto para {isInputMode ? 'ingreso' : 'salida'}...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
